// The module contract (roadmap §3.2), as a zod schema in plain JS so that both the
// TypeScript app and the Node checker (tools/check-modules.js) validate the same thing.
// A module is a folder with a manifest.json that satisfies this schema.
import { z } from 'zod';

const kebab = z.string().regex(/^[a-z][a-z0-9-]*$/, 'kebab-case id');

export const NavEntry = z.object({
  id: kebab,
  label: z.string().min(1).max(24),
  icon: z.string().min(1),                       // icon name from the design system set
  where: z.array(z.enum(['phone', 'desktop'])).min(1),
  order: z.number().int().min(0).max(999),
  route: z.string().startsWith('/'),
  // The room this entry belongs to (30-day row 30): a job's menu, a bid's menu, both, or the company's list outside
  // any room. The shell reads this; it holds no list of module ids. bidLabel is the word a bid uses for it.
  // 'job', 'company', 'both' (job and bid), or the id of a room some module declares (bid, service, warehouse…); the checker validates.
  room: kebab.default('job'),
  bidLabel: z.string().min(1).max(24).optional(),
  // In a room of projects: 'one' = a page of the picked job or bid (the default); 'room' = a page of the whole room
  // (every bid, every job: its schedule), shown above the picked one and before any is picked.
  scope: z.enum(['one', 'room']).default('one'),
});

export const Route = z.object({
  path: z.string().startsWith('/'),
  screen: z.string().min(1),                     // export name from the module's entry file
  roles: z.array(z.string()).optional(),         // omit = every role the module allows
});

export const Collection = z.object({
  name: kebab,
  scope: z.enum(['company', 'project', 'user']),
  description: z.string().min(1),
});

export const Notification = z.object({
  id: kebab,
  label: z.string().min(1),
  defaultChannels: z.array(z.enum(['push', 'bell', 'email'])).min(1),
  roles: z.array(z.string()).optional(),
});

export const Sku = z.object({
  price: z.number().min(0),                      // placeholder until the Phase 6 pricing study
  unit: z.enum(['company', 'field-seat', 'office-seat', 'included']),
  trialDays: z.number().int().min(0).default(14),
});

export const Marketing = z.object({
  tagline: z.string().min(10).max(160),
  bullets: z.array(z.string().min(10)).min(2).max(5),
  goodFor: z.string().min(10),
  screenshots: z.array(z.string()).default([]),
});

// A record type the module holds, per docs/RECORD-STANDARD.md: its editor (which accepts
// ?r=<id> to continue a draft), its page, its number prefix, and whether there is one per
// job per day. The checker fails the build when a declared route does not exist.
export const RecordType = z.object({
  type: kebab,
  label: z.string().min(2),
  editor: z.string().startsWith('/'),
  page: z.string().startsWith('/'),
  prefix: z.string().regex(/^[A-Z]{2,3}$/),
  onePerDay: z.boolean().default(false),
});

export const Manifest = z.object({
  id: kebab,
  name: z.string().min(2).max(40),
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  bundle: z.enum(['core', 'field', 'office', 'money', 'insight']),
  sku: Sku,
  roles: z.array(z.string()).min(1),             // role ids from @tradeview/shared that may use it
  dependencies: z.array(kebab).default([]),
  entry: z.string().min(1),                      // relative path to the module's screen exports
  nav: z.array(NavEntry).default([]),
  routes: z.array(Route).min(1),
  collections: z.array(Collection).default([]),
  records: z.array(RecordType).default([]),
  notifications: z.array(Notification).default([]),
  settings: z.record(z.string(), z.unknown()).default({}),
  onboarding: z.array(z.string().min(5)).default([]),
  marketing: Marketing,
  aiTools: z.array(kebab).default([]),
  aiDrafts: z.array(kebab).default([]),
  // Rooms this module brings into the shell: a module that declares 'bid' makes the Jobs | Bids switch exist for the
  // company that has it. Without such a module the shell never says the word.
  // Rooms this module brings into the shell (30-day row 31): each with its word, its front door (one of the module's
  // routes; ':id' is the picked project when the room picks one) and, for a room of projects, which kind it picks.
  // The core brings Jobs and Company; Bids brings Bids; Service and Warehouse bring theirs. A company without the
  // module never sees the room.
  rooms: z.array(z.object({ id: kebab, label: z.string().min(1).max(24), door: z.string().startsWith('/'), picks: z.enum(['job', 'bid']).optional() })).default([]),
  // The export name (from the entry file) of the module's health function for the job's front door: the tiles and the
  // next things this module contributes for one job. Only modules the company has contribute.
  health: z.string().min(1).optional(),
});

export const PhaseStates = ['locked', 'trial', 'empty', 'active'];
